/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
import { buildBackendUrl } from '../utils/backendUrl';
import { getAuthToken } from './authStorage';
import { apiPost } from './apiClient';

const EXIT_SIGNAL_URL = buildBackendUrl('/api/backup/exit-signal');
const HEARTBEAT_URL = buildBackendUrl('/api/backup/session-heartbeat');

const safeHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

export const sendExitBackupSignal = (trigger = 'browser_close', extra = {}) => {
  try {
    const token = getAuthToken('erp');
    if (!token) return false;
    const body = JSON.stringify({
      trigger_type: trigger,
      path: typeof window !== 'undefined' ? window.location.pathname : '',
      at: new Date().toISOString(),
      ...extra,
    });
    // fetch keepalive can carry Authorization headers; sendBeacon cannot.
    fetch(EXIT_SIGNAL_URL, {
      method: 'POST',
      headers: safeHeaders(token),
      body,
      keepalive: true,
      credentials: 'include',
    }).catch(() => {});
    return true;
  } catch (_) {
    return false;
  }
};

export const sendBackupHeartbeat = (extra = {}) => {
  try {
    const token = getAuthToken('erp');
    if (!token) return false;
    fetch(HEARTBEAT_URL, {
      method: 'POST',
      headers: safeHeaders(token),
      body: JSON.stringify({
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        visibility_state: typeof document !== 'undefined' ? document.visibilityState : '',
        at: new Date().toISOString(),
        ...extra,
      }),
      keepalive: false,
      credentials: 'include',
    }).catch(() => {});
    return true;
  } catch (_) {
    return false;
  }
};

export const runMandatoryLogoutBackup = async () => {
  return apiPost('/backup/exit-backup', {
    trigger_type: 'logout',
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    at: new Date().toISOString(),
  }, { timeout: 300000, skipAuthRedirect: true, authContext: 'erp' });
};

export const installExitBackupBrowserGuards = () => {
  if (typeof window === 'undefined') return () => {};
  let sent = false;
  const fireOnce = (trigger) => {
    if (sent) return;
    sent = true;
    sendExitBackupSignal(trigger);
    window.setTimeout(() => { sent = false; }, 30000);
  };
  const onPageHide = () => fireOnce('pagehide');
  const onBeforeUnload = () => fireOnce('beforeunload');
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      sendExitBackupSignal('browser_close', { visibility_state: 'hidden' });
    }
  };
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('beforeunload', onBeforeUnload);
  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('beforeunload', onBeforeUnload);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};
